// Lot 685 — lecteur PARTAGÉ des publications SON (fil, profil, page publication) + mini-lecteur flottant.
// Pourquoi un lecteur partagé : avec le fil allégé (lot 677), une publication à plus de 2 écrans est DÉMONTÉE —
// son <audio> disparaîtrait et le son s'arrêterait net en défilant. Ici un seul <audio> (dans le document, donc vu
// par « un seul son à la fois » et le silence en arrière-plan du lot 684) vit indépendamment des cartes : la carte
// n'est qu'une vue/télécommande de ce lecteur quand c'est sa piste ; la barre NowPlayingBar prend le relais
// quand la carte en cours n'est plus à l'écran.
import { useSyncExternalStore } from "react";
import { primeResume, getResume, saveProgress, resumeFinished, RESUME_MIN_SECONDS } from "@/lib/videoProgress";

export const TRACK_SPEEDS = [1, 1.5, 2];
// Garde-fou admin (guards.js) : coupé → lecteur local dans la carte (comportement d'avant) et aucune barre flottante.
export const SHARED_PLAYER_GUARD = "shared_audio_player";
// Lot 686 — reprise : la position d'écoute est mémorisée sur le COMPTE (même mécanisme que les vidéos : lib/videoProgress,
// PUT /videos/progress, délai « mémoire de position » admin) quand track.resume est vrai (membre connecté + réglage admin).
const SAVE_EVERY_MS = 5000;
let el = null;
let globalBound = false;
let lastSaveAt = 0;
let pendingSeek = null;
// track : { id, url, title, subtitle, avatar, duration } — id = id de la publication.
let state = { track: null, playing: false, cur: 0, dur: 0, rate: 1, cardVisible: true, ended: false };
// Instantané stable pour les cartes qui ne sont PAS la piste en cours (ne change qu'avec la vitesse) :
// aucune re-rendu de 15 cartes 4×/s pendant la lecture.
let idle = Object.freeze({ track: null, playing: false, cur: 0, dur: 0, rate: 1, cardVisible: true, ended: false });
const listeners = new Set();
const emit = () => listeners.forEach((fn) => { try { fn(); } catch { /* noop */ } });
const set = (patch) => {
  state = { ...state, ...patch };
  if (patch.rate !== undefined && patch.rate !== idle.rate) idle = Object.freeze({ ...idle, rate: patch.rate });
  emit();
};

export const subscribeNowPlaying = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
export const getNowPlaying = () => state;
export const isCurrentTrack = (id) => !!id && state.track?.id === id;
export const useNowPlaying = () => useSyncExternalStore(subscribeNowPlaying, getNowPlaying, getNowPlaying);
// État vu par UNE carte : l'état réel si c'est sa piste, sinon l'instantané stable.
export const useTrackState = (id) => useSyncExternalStore(subscribeNowPlaying, () => (isCurrentTrack(id) ? state : idle), () => idle);

// Position courante → mémoire du compte (si la piste le demande) ; `flush` = envoi immédiat (pause, changement, sortie).
const recordProgress = (flush) => {
  const tr = state.track;
  if (!el || !tr?.resume) return;
  const t = el.currentTime;
  if (!(t > 0)) return;
  const d = Number.isFinite(el.duration) && el.duration > 0 ? el.duration : (state.dur > 0 ? state.dur : null);
  saveProgress(tr.id, t, d, flush);
};
const applyPendingSeek = () => {
  if (pendingSeek == null || !el) return;
  const t = pendingSeek;
  pendingSeek = null;
  try { el.currentTime = t; } catch { /* noop */ }
  set({ cur: t });
};

const ensureEl = () => {
  if (el || typeof document === "undefined") return el;
  el = document.createElement("audio");
  el.preload = "metadata";
  el.className = "hidden";
  el.setAttribute("data-testid", "now-playing-audio");
  el.addEventListener("play", () => set({ playing: true, ended: false }));
  el.addEventListener("pause", () => { set({ playing: false }); recordProgress(true); });
  el.addEventListener("ended", () => { recordProgress(true); set({ playing: false, cur: 0, ended: true }); });
  el.addEventListener("timeupdate", () => {
    set({ cur: el.currentTime || 0 });
    const now = Date.now();
    if (now - lastSaveAt >= SAVE_EVERY_MS) { lastSaveAt = now; recordProgress(false); }
  });
  const onDur = () => { const d = el.duration; if (Number.isFinite(d) && d > 0) set({ dur: d }); applyPendingSeek(); };
  el.addEventListener("loadedmetadata", onDur);
  el.addEventListener("durationchange", onDur);
  if (!globalBound) {
    globalBound = true;
    // L'app se ferme ou passe en arrière-plan : la dernière position part tout de suite.
    window.addEventListener("pagehide", () => recordProgress(true));
    document.addEventListener("visibilitychange", () => { if (document.visibilityState === "hidden") recordProgress(true); });
  }
  document.body.appendChild(el);
  return el;
};

// Charge la piste sans la lancer (nouvelle piste → position mémorisée s'il y en a une, sinon 0 ; carte supposée
// visible : le membre vient de la toucher). La piste précédente enregistre sa position avant d'être remplacée.
export const loadTrack = (track) => {
  const a = ensureEl();
  if (!a || !track?.id || !track.url) return null;
  if (state.track?.id !== track.id) {
    recordProgress(true);
    a.src = track.url;
    let start = 0;
    if (track.resume) {
      primeResume(track.id, Number(track.resumeAt) || 0);
      const t = getResume(track.id);
      if (t >= RESUME_MIN_SECONDS && !resumeFinished(t, track.duration > 0 ? track.duration : 0)) start = t;
    }
    pendingSeek = start > 0 ? start : null;
    set({ track, cur: start, dur: track.duration > 0 ? track.duration : 0, ended: false, cardVisible: true });
    if (start > 0) { try { a.currentTime = start; } catch { /* appliqué à loadedmetadata */ } }
  } else if (track.duration > 0 && !state.dur) {
    set({ dur: track.duration });
  }
  return a;
};

// Lecture refusée (source non chargée, lecture annulée par un seek) → rechargement + une relance, puis silence.
export const playTrack = (track, { at } = {}) => {
  const a = loadTrack(track);
  if (!a) return;
  if (Number.isFinite(at)) { pendingSeek = null; try { a.currentTime = at; } catch { /* noop */ } set({ cur: at }); }
  a.playbackRate = state.rate;
  const p = a.play();
  if (!p?.catch) return;
  p.catch(() => {
    try { a.load(); if (Number.isFinite(at)) a.currentTime = at; } catch { /* noop */ }
    const again = a.play();
    if (again?.catch) again.catch(() => {});
  });
};
export const pauseTrack = () => { try { el?.pause(); } catch { /* noop */ } };
export const toggleTrack = (track) => { if (isCurrentTrack(track?.id) && state.playing) pauseTrack(); else playTrack(track); };
export const seekTrack = (track, t) => {
  const a = loadTrack(track);
  if (!a) return;
  pendingSeek = null;
  try { a.currentTime = t; } catch { /* noop */ }
  set({ cur: t });
};
export const setTrackRate = (rate) => {
  const r = TRACK_SPEEDS.includes(rate) ? rate : 1;
  set({ rate: r });
  if (el) el.playbackRate = r;
};
// Durée arrivée du serveur (forme d'onde) après le démarrage : complète la piste en cours si l'élément ne la connaît pas.
export const setTrackDuration = (id, duration) => { if (isCurrentTrack(id) && duration > 0 && !state.dur) set({ dur: duration }); };
// La carte de la piste en cours entre/sort de l'écran (ou est démontée) → la barre flottante prend / rend le relais.
export const setTrackCardVisible = (id, visible) => { if (isCurrentTrack(id) && state.cardVisible !== !!visible) set({ cardVisible: !!visible }); };
export const stopTrack = () => {
  recordProgress(true);
  pendingSeek = null;
  if (el) { try { el.pause(); el.removeAttribute("src"); el.load(); } catch { /* noop */ } }
  set({ track: null, playing: false, cur: 0, dur: 0, ended: false, cardVisible: true });
};

export const _resetNowPlayingForTests = () => {
  if (el) { try { el.remove(); } catch { /* noop */ } }
  el = null;
  pendingSeek = null;
  lastSaveAt = 0;
  state = { track: null, playing: false, cur: 0, dur: 0, rate: 1, cardVisible: true, ended: false };
  idle = Object.freeze({ ...idle, rate: 1 });
  listeners.clear();
};
